import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { ToasterService } from '@abp/ng.theme.shared';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import {
  conditionalValidator,
  DisplayNamePipe,
  DwInputRequiredDirective,
  ErrorFormControlDirective,
} from '@digiwall/core';
import { LocalizationModule } from '@abp/ng.core';
import { DocRole } from '@digiwall/goliath-core/proxy';
import {
  DayBookDto,
  DayBookService,
  CustomerSalesPurchaseService,
} from '@digiwall/goliath-documents/proxy';
import { NgSelectComponent } from '@ng-select/ng-select';
import { GoliathCoreEnumerationTypeStore } from '@digiwall/goliath-core';
import { DocumentCloneHelperService } from 'projects/goliath-documents/src/lib/services/documents/document-clone-helper.service';

@Component({
  selector: 'document-sales-purchase-dialog-box-clone',
  templateUrl: 'dialog-box-clone.component.html',
  styleUrl: 'dialog-box-clone.component.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    ErrorFormControlDirective,
    LocalizationModule,
    NgSelectComponent,
    DwInputRequiredDirective,
    DisplayNamePipe,
  ],
})
export class SalesPurchaseDialogBoxCloneComponent implements OnInit {
  enumerationTypeStore = inject(GoliathCoreEnumerationTypeStore);

  @Input() documentId: string;
  @Input() fromList: boolean;

  _areAnnexesIncluded: boolean;
  get areAnnexesIncluded(): boolean {
    return this._areAnnexesIncluded;
  }
  @Input() set areAnnexesIncluded(value: boolean) {
    this._areAnnexesIncluded = value;
    if (!this._areAnnexesIncluded) {
      this.form?.get('copyAnnexes').disable();
    }
  }
  @Input() isPriceComputationAvailable: boolean = false;

  form: FormGroup;
  DocRole = DocRole;
  targetedDocRole = DocRole.CustomerSalesPurchase;
  daybooks: DayBookDto[] = [];

  constructor(
    private docService: CustomerSalesPurchaseService,
    protected toastService: ToasterService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected formBuilder: FormBuilder,
    protected modal: NgbActiveModal,
    protected changeDetector: ChangeDetectorRef,
    protected daybookService: DayBookService,
    private cloneHelper: DocumentCloneHelperService,
  ) {
    this.daybookService.getDayBooksByRole(DocRole.CustomerQuote).subscribeOnce(daybooks => {
      this.daybooks = daybooks;
    });
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.form = this.formBuilder.group({
      reevaluateTariff: [false],
      copyAnnexes: [false],
      daybookId: [
        null,
        conditionalValidator(
          () => this.targetedDocRole === DocRole.CustomerQuote,
          Validators.required,
        ),
      ],
    });
  }

  protected clone() {
    this.modal.close();

    if (this.targetedDocRole == DocRole.CustomerQuote) {
      this.docService
        .cloneToQuote(
          this.documentId,
          this.form.get('copyAnnexes').value,
          this.isPriceComputationAvailable ? this.form.get('reevaluateTariff').value : false,
          this.form.get('daybookId').value,
        )
        .subscribeOnce(response => {
          this.toastService.success('Doc::Duplicated');
          this.router.navigate([this.fromList ? '../quotes' : '../../quotes', response.id], {
            relativeTo: this.route,
          });
        });
    } else if (this.targetedDocRole == DocRole.CustomerSalesPurchase) {
      this.cloneHelper
        .clone(this.docService.cloneWithAnnexesAndReevaluateTariff, {
          docId: this.documentId,
          withAnnexes: this.form.get('copyAnnexes').value,
          reevaluateTariff: this.isPriceComputationAvailable
            ? this.form.get('reevaluateTariff').value
            : false,
          replaceInvalidArticles: false,
        })
        .subscribeOnce(response => {
          this.toastService.success('Doc::Duplicated');
          this.router.navigate([this.fromList ? './' : '../', response.id], {
            relativeTo: this.route,
          });
        });
    }
  }

  protected setTargetedDocRole(role: DocRole) {
    this.targetedDocRole = role;
    this.form
      .get('daybookId')
      .setValue(role == DocRole.CustomerQuote ? this.daybooks[0]?.id : null);
    this.form.get('daybookId').updateValueAndValidity();
    this.changeDetector.detectChanges();
  }
}
